import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserDeletionImpact, UserResponse } from '@shared/schemas';

import { initialState } from './administrators.state';

/**
 * Administrators index slice. Reducers are pure state transitions only; the
 * fetch and the archive PATCH live in the saga, which dispatches these `load*` /
 * `archive*` transitions as it progresses.
 */
const slice = createSlice({
  name: 'administrators',
  initialState,
  reducers: {
    loadStarted: (state) => {
      state.loading = true;
      state.loadError = null;
    },
    loadSucceeded: (state, action: PayloadAction<UserResponse[]>) => {
      state.rows = action.payload;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.loadError = action.payload;
    },
    // ── Archive lifecycle ──
    archiveStarted: (state) => {
      state.archiveStatus = 'saving';
      // Cleared on each attempt, so a stale message cannot outlive the run that
      // produced it (PI-50).
      state.archiveError = null;
    },
    /** Patch the affected row's `archived_at` in place on success. */
    archiveSucceeded: (
      state,
      action: PayloadAction<{ id: string; archivedAt: string | null }>,
    ) => {
      state.archiveStatus = 'succeeded';
      const row = state.rows.find((u) => u.id === action.payload.id);
      if (row) row.archived_at = action.payload.archivedAt;
    },
    archiveFailed: (state, action: PayloadAction<string>) => {
      state.archiveStatus = 'failed';
      state.archiveError = action.payload;
    },
    /**
     * The page has shown the outcome. Returns the lifecycle to rest so the next
     * attempt is a fresh `saving → settled` transition rather than a repeat of
     * the last one; which is what the page's snackbar effect watches for.
     */
    archiveCleared: (state) => {
      state.archiveStatus = 'idle';
      state.archiveError = null;
    },
    // ── Delete lifecycle (task #587); the archive one's shape ──
    deleteStarted: (state) => {
      state.deleteStatus = 'saving';
      state.deleteError = null;
    },
    /** Drop the deleted row: there is nothing left on the server to refetch. */
    deleteSucceeded: (state, action: PayloadAction<string>) => {
      state.deleteStatus = 'succeeded';
      state.rows = state.rows.filter((u) => u.id !== action.payload);
    },
    deleteFailed: (state, action: PayloadAction<string>) => {
      state.deleteStatus = 'failed';
      state.deleteError = action.payload;
    },
    deleteCleared: (state) => {
      state.deleteStatus = 'idle';
      state.deleteError = null;
    },
    /** A new target: forget the last one's figures before counting its own. */
    deletionImpactStarted: (state) => {
      state.deletionImpact = null;
    },
    deletionImpactLoaded: (state, action: PayloadAction<UserDeletionImpact>) => {
      state.deletionImpact = action.payload;
    },
  },
});

export const administratorsActions = slice.actions;
export const administratorsReducer = slice.reducer;
